import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Badge, Popover, Grid } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  LogoutOutlined,
  BellOutlined,
  WalletOutlined,
  PercentageOutlined,
  TransactionOutlined,
  BankOutlined,
  BarChartOutlined,
  ApiOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../utils/axios';

const { Header, Sider, Content } = Layout;

const isAdminUser = (user) =>
  Boolean(user?.isAdmin || user?.role === 'admin' || user?.role === 'super_admin');

/** Sidebar label with a right-aligned count pill. */
const navLabel = (text, count, tone) => (
  <span className="nav-label">
    <span className="nav-label__text">{text}</span>
    {count > 0 ? (
      <span className={`nav-count${tone === 'warning' ? ' nav-count--warning' : ''}`}>
        {count > 99 ? '99+' : count}
      </span>
    ) : null}
  </span>
);

/** Route → [group, page] for the header breadcrumb. */
const PAGE_TITLES = {
  '/admin/dashboard': ['Overview', 'Dashboard'],
  '/admin/vouchers': ['Catalogue', 'Vouchers'],
  '/admin/supply-dashboard': ['Catalogue', 'Supply dashboard'],
  '/admin/pricing-tiers': ['Catalogue', 'Pricing tiers'],
  '/admin/orders': ['Orders', 'Orders'],
  '/admin/supplier-ops': ['Orders', 'Supplier ops'],
  '/admin/clients': ['Clients & finance', 'Clients'],
  '/admin/topup-requests': ['Clients & finance', 'Top-up requests'],
  '/admin/repayment-requests': ['Clients & finance', 'Repayment requests'],
  '/client/dashboard': [null, 'Overview'],
  '/client/vouchers': [null, 'Vouchers'],
  '/client/orders': [null, 'Orders'],
  '/client/account/transactions': [null, 'Transactions'],
  '/client/account/repayment': [null, 'Repay credit'],
  '/client/account/topup': [null, 'Top up'],
  '/client/account/profile': [null, 'My account'],
};

const findPageTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const prefix = Object.keys(PAGE_TITLES)
    .filter((key) => pathname.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? PAGE_TITLES[prefix] : [null, null];
};

const AppLayout = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [collapsed, setCollapsed] = useState(false);
  const [todoOpen, setTodoOpen] = useState(false);
  const [counts, setCounts] = useState({
    topUps: 0,
    repayments: 0,
    orders: 0,
    uncertainOrders: 0,
    callbacks: 0,
    clients: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = isAdminUser(user);

  // Collapse to icons on tablets; hide entirely on phones.
  useEffect(() => {
    if (screens.lg === false) setCollapsed(true);
    if (screens.lg) setCollapsed(false);
  }, [screens.lg]);

  // Queue sizes for the sidebar badges and the header to-do list (admin only).
  const fetchAdminPendingCounts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [topUpRes, repaymentRes, healthRes, clientRes] = await Promise.all([
        axios.get('/admin/topup-requests', { params: { page: 1, limit: 1 } }),
        axios.get('/admin/repayment-requests', { params: { status: 'pending' } }),
        axios.get('/admin/external-orders/health').catch(() => null),
        axios.get('/admin/clients/statistics').catch(() => null),
      ]);
      const repaySummary = repaymentRes.data.summary;
      const repayList = repaymentRes.data.requests;
      const needs = healthRes?.data?.data?.needsAttention || {};
      setCounts({
        topUps: topUpRes.data.statistics?.pending ?? 0,
        repayments: repaySummary?.pending ?? repayList?.length ?? 0,
        orders: (needs.uncertainOrders || 0) + (needs.refundFailedOrders || 0),
        uncertainOrders: needs.uncertainOrders || 0,
        callbacks: needs.unresolvedDroppedCallbacks || 0,
        clients: clientRes?.data?.pending || 0,
      });
    } catch (error) {
      console.error('Error fetching admin pending counts:', error);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAdminPendingCounts();
    const interval = setInterval(fetchAdminPendingCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchAdminPendingCounts]);

  // Refresh the queue sizes after leaving a page, so a request approved a
  // moment ago no longer shows in the badge.
  useEffect(() => {
    fetchAdminPendingCounts();
  }, [location.pathname, fetchAdminPendingCounts]);

  const handleLogout = () => {
    logout();
    navigate(isAdmin ? '/admin/login' : '/login');
  };

  const menuItems = useMemo(() => {
    if (isAdmin) {
      return [
        { key: '/admin/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
        {
          type: 'group',
          key: 'g-catalogue',
          label: 'Catalogue',
          children: [
            { key: '/admin/vouchers', icon: <ShoppingOutlined />, label: 'Vouchers' },
            { key: '/admin/supply-dashboard', icon: <BarChartOutlined />, label: 'Supply dashboard' },
            { key: '/admin/pricing-tiers', icon: <PercentageOutlined />, label: 'Pricing tiers' },
          ],
        },
        {
          type: 'group',
          key: 'g-orders',
          label: 'Orders',
          children: [
            {
              key: '/admin/orders',
              icon: <FileTextOutlined />,
              label: navLabel('Orders', counts.orders),
            },
            {
              key: '/admin/supplier-ops',
              icon: <ApiOutlined />,
              label: navLabel('Supplier ops', counts.callbacks),
            },
          ],
        },
        {
          type: 'group',
          key: 'g-finance',
          label: 'Clients & finance',
          children: [
            {
              key: '/admin/clients',
              icon: <TeamOutlined />,
              label: navLabel('Clients', counts.clients, 'warning'),
            },
            {
              key: '/admin/topup-requests',
              icon: <WalletOutlined />,
              label: navLabel('Top-up requests', counts.topUps, 'warning'),
            },
            {
              key: '/admin/repayment-requests',
              icon: <BankOutlined />,
              label: navLabel('Repayment requests', counts.repayments, 'warning'),
            },
          ],
        },
      ];
    }
    if (user?.role?.startsWith('client')) {
      return [
        { key: '/client/dashboard', icon: <DashboardOutlined />, label: 'Overview' },
        { key: '/client/vouchers', icon: <ShoppingOutlined />, label: 'Vouchers' },
        { key: '/client/orders', icon: <FileTextOutlined />, label: 'Orders' },
        { key: '/client/account/transactions', icon: <TransactionOutlined />, label: 'Transactions' },
        { key: '/client/account/repayment', icon: <BankOutlined />, label: 'Repay credit' },
      ];
    }
    return [];
  }, [isAdmin, user?.role, counts]);

  // Highlight the parent entry on detail routes such as /client/orders/:id.
  const selectedKey = useMemo(() => {
    const keys = [];
    const walk = (items) =>
      items.forEach((item) => {
        if (item.children) walk(item.children);
        else if (item.key) keys.push(item.key);
      });
    walk(menuItems);
    return (
      keys
        .filter((key) => location.pathname === key || location.pathname.startsWith(`${key}/`))
        .sort((a, b) => b.length - a.length)[0] || location.pathname
    );
  }, [menuItems, location.pathname]);

  const todoItems = [
    {
      key: 'topups',
      label: 'Top-up requests to review',
      icon: <WalletOutlined />,
      count: counts.topUps,
      to: '/admin/topup-requests',
    },
    {
      key: 'repayments',
      label: 'Repayments to review',
      icon: <BankOutlined />,
      count: counts.repayments,
      to: '/admin/repayment-requests',
    },
    {
      key: 'clients',
      label: 'Client registrations to review',
      icon: <TeamOutlined />,
      count: counts.clients,
      to: '/admin/clients',
      state: { filterStatus: 'pending' },
    },
    {
      key: 'orders',
      label: 'Orders needing a person',
      icon: <FileTextOutlined />,
      count: counts.orders,
      to: '/admin/orders',
      state: { attention: counts.uncertainOrders > 0 ? 'uncertain' : 'refund_failed' },
    },
    {
      key: 'callbacks',
      label: 'Dropped supplier callbacks',
      icon: <ApiOutlined />,
      count: counts.callbacks,
      to: '/admin/supplier-ops',
    },
  ];
  const todoTotal = todoItems.reduce((sum, item) => sum + (item.count || 0), 0);

  const todoContent = (
    <div className="todo-pop">
      <div className="todo-pop__head">Needs action</div>
      {todoTotal === 0 ? (
        <div className="todo-pop__empty">Nothing waiting. All queues are clear.</div>
      ) : null}
      {todoItems
        .filter((item) => todoTotal === 0 || item.count > 0)
        .map((item) => (
          <div
            key={item.key}
            className="todo-pop__item"
            role="button"
            tabIndex={0}
            onClick={() => {
              setTodoOpen(false);
              navigate(item.to, item.state ? { state: item.state } : undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setTodoOpen(false);
                navigate(item.to, item.state ? { state: item.state } : undefined);
              }
            }}
          >
            <span className="todo-pop__item-label">
              {item.icon}
              {item.label}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className={`todo-pop__count${item.count > 0 ? ' is-hot' : ''}`}>{item.count}</span>
              <RightOutlined style={{ fontSize: 10, color: 'var(--c-text-3)' }} />
            </span>
          </div>
        ))}
    </div>
  );

  const userMenuItems = [
    ...(isAdmin
      ? []
      : [
          {
            key: 'account',
            icon: <UserOutlined />,
            label: 'My account',
            onClick: () => navigate('/client/account/profile'),
          },
          { type: 'divider' },
        ]),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      onClick: handleLogout,
    },
  ];

  const [crumbGroup, crumbPage] = findPageTitle(location.pathname);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;
  const roleLabel = isAdmin
    ? String(user?.role || 'admin').replace(/_/g, ' ')
    : user?.companyName || user?.name || 'Client';

  return (
    <Layout className="app-shell" hasSider>
      <Sider
        className="app-sider"
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={232}
        collapsedWidth={isMobile ? 0 : 64}
      >
        <div
          className="app-sider__brand"
          onClick={() => navigate(isAdmin ? '/admin/dashboard' : '/client/dashboard')}
        >
          <img
            src={collapsed ? '/favicon.png' : '/images/vouchain-logo.png'}
            alt="Vouchain"
          />
        </div>
        <div className="app-sider__menu">
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            items={menuItems}
            onClick={({ key }) => {
              navigate(key);
              if (isMobile) setCollapsed(true);
            }}
          />
        </div>
        <div className="app-sider__foot">{isAdmin ? 'Vouchain Admin' : 'Vouchain'}</div>
      </Sider>

      <Layout style={{ minWidth: 0 }}>
        <Header className="app-header">
          <div className="app-header__left">
            <Button
              type="text"
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
            {crumbPage ? (
              <div className="app-header__crumbs">
                {crumbGroup && crumbGroup !== crumbPage ? (
                  <>
                    <span className="app-header__crumbs-group">{crumbGroup}</span>
                    <span className="app-header__crumbs-sep">/</span>
                  </>
                ) : null}
                <span className="app-header__crumbs-page">{crumbPage}</span>
              </div>
            ) : null}
          </div>

          <div className="app-header__right">
            {isAdmin ? (
              <Popover
                trigger="click"
                placement="bottomRight"
                open={todoOpen}
                onOpenChange={(open) => {
                  setTodoOpen(open);
                  if (open) fetchAdminPendingCounts();
                }}
                content={todoContent}
                arrow={false}
              >
                <Badge count={todoTotal} size="small" offset={[-4, 4]}>
                  <Button type="text" aria-label="Needs action" icon={<BellOutlined />} />
                </Badge>
              </Popover>
            ) : null}

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <div className="app-user">
                <Avatar size={32} style={{ background: 'var(--c-primary)' }} icon={<UserOutlined />} />
                {!isMobile ? (
                  <div>
                    <div className="app-user__name">{displayName}</div>
                    <div className="app-user__role">{roleLabel}</div>
                  </div>
                ) : null}
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className={`app-content${isAdmin ? '' : ' app-content--panel'}`}>
          {isAdmin ? (
            <div className="app-page">{children}</div>
          ) : (
            <div className="app-panel">{children}</div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
